// components/AdminPanel.js — панель администрирования (только для isSuperAdmin)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton,
  Button, CircularProgress, LinearProgress, Tabs, Tab,
  Select, MenuItem, FormControl,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import HistoryIcon from '@mui/icons-material/History';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import SaveIcon from '@mui/icons-material/Save';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import PeopleIcon from '@mui/icons-material/People';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldIcon from '@mui/icons-material/Shield';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import BarChartIcon from '@mui/icons-material/BarChart';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { collection, getDocs, collectionGroup, doc, updateDoc, deleteDoc, onSnapshot, query, orderBy, limit, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { loadDrawingList, loadDrawingData, isCloudDrawing, deleteAllDeviceDrawingCopiesGlobally } from '../services/drawingStorage';
import { sessionDurationForStats } from '../services/logActivity';

const FIRESTORE_FREE_LIMIT_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB
const ONLINE_THRESHOLD_MS = 3 * 60 * 1000; // 3 минуты

function fmtBytes(b) {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(3)} GB`;
}

function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate ? val.toDate() : new Date(val);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

function isOnline(lastSeen) {
  if (!lastSeen) return false;
  const d = lastSeen?.toDate ? lastSeen.toDate() : new Date(lastSeen);
  return Date.now() - d.getTime() < ONLINE_THRESHOLD_MS;
}

const ROLE_COLORS = { admin: '#d32f2f', engineer: '#1565c0', user: '#616161' };
const ROLE_SEARCH_LABELS = { admin: 'администратор', engineer: 'инженер', user: 'пользователь' };

function userMatchesSearch(u, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    u.email,
    u.initials,
    u.uid,
    u.role,
    ROLE_SEARCH_LABELS[u.role],
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(q);
}

// ─── Вкладка: Хранилище ───────────────────────────────────────────────────────
function StorageTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    setStats(null);
    try {
      const snap = await getDocs(collectionGroup(db, 'drawings'));
      let totalBytes = 0;
      let totalDocs = 0;
      const perUser = {};
      snap.forEach((d) => {
        const data = d.data();
        const bytes = data.byteSize || 0;
        const uid = d.ref.parent.parent?.id || 'unknown';
        totalBytes += bytes;
        totalDocs++;
        if (!perUser[uid]) perUser[uid] = { count: 0, bytes: 0 };
        perUser[uid].count++;
        perUser[uid].bytes += bytes;
      });
      setStats({ totalBytes, totalDocs, perUser });
    } catch (e) {
      setStats({ error: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const pct = stats ? Math.min((stats.totalBytes / FIRESTORE_FREE_LIMIT_BYTES) * 100, 100) : 0;
  const barColor = pct > 80 ? '#d32f2f' : pct > 50 ? '#f57c00' : '#43a047';

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />} onClick={fetch} disabled={loading} sx={{ fontSize: '0.75rem', color: '#9e9e9e' }}>Обновить</Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: '#d32f2f' }} /></Box>}
      {!loading && stats?.error && <Typography sx={{ fontSize: '0.82rem', color: '#d32f2f' }}>Ошибка: {stats.error}</Typography>}

      {!loading && stats && !stats.error && (
        <>
          <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', p: 2 }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.25 }}>Итого</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#616161' }}>Чертежей всего</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#212121' }}>{stats.totalDocs}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.25 }}>
              <Typography sx={{ fontSize: '0.82rem', color: '#616161' }}>Занято места</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#212121' }}>{fmtBytes(stats.totalBytes)}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct}
              sx={{ height: '0.5rem', borderRadius: '0.25rem', background: '#f0f0f0', '& .MuiLinearProgress-bar': { background: barColor } }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', mt: 0.75, textAlign: 'right' }}>
              {pct.toFixed(2)}% от 1 GB (бесплатный лимит Firestore)
            </Typography>
          </Box>

          <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', px: 2, pt: 1.5, pb: 1 }}>По пользователям</Typography>
            {Object.entries(stats.perUser).sort((a, b) => b[1].bytes - a[1].bytes).map(([uid, info], idx, arr) => (
              <Box key={uid} sx={{ px: 2, py: 1, borderTop: '0.0625rem solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: '#9e9e9e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '18rem' }}>{uid}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#616161' }}>{info.count} чертёж(ей)</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#212121', flexShrink: 0, ml: 1 }}>{fmtBytes(info.bytes)}</Typography>
              </Box>
            ))}
            {Object.keys(stats.perUser).length === 0 && (
              <Typography sx={{ fontSize: '0.8rem', color: '#bdbdbd', p: 2 }}>Нет данных</Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(val) {
  if (!val) return '—';
  const d = val?.toDate ? val.toDate() : new Date(val);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

// ─── Диалог чертежей конкретного пользователя (для суперадмина) ────────────────
function UserDrawingsDialog({ open, onClose, targetUser, onOpenDrawing }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const filteredList = search.trim()
    ? list.filter(e => (e.fileName || '').toLowerCase().includes(search.trim().toLowerCase()))
    : list;

  const cloudList = filteredList.filter(isCloudDrawing);
  const deviceList = filteredList.filter((e) => !isCloudDrawing(e));

  const fetchList = useCallback(async () => {
    if (!targetUser?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const items = await loadDrawingList(targetUser.uid);
      setList(items);
    } catch (e) {
      setError('Не удалось загрузить чертежи');
    } finally {
      setLoading(false);
    }
  }, [targetUser?.uid]);

  useEffect(() => { if (open) { setSearch(''); fetchList(); } }, [open, fetchList]);

  const handleDownload = async (entry) => {
    setActionId(entry.id);
    try {
      if (entry.fileFormat === 'pdf' && entry.pdfBase64) {
        const binary = atob(entry.pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${entry.fileName}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const data = await loadDrawingData(entry);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${entry.fileName}.ptCAD`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch { setError('Не удалось скачать'); }
    finally { setActionId(null); }
  };

  const handleViewPdf = (entry) => {
    if (!entry.pdfBase64) return;
    try {
      const binary = atob(entry.pdfBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Не удалось открыть PDF');
    }
  };

  const handleOpen = async (entry) => {
    setActionId(entry.id);
    try {
      const data = await loadDrawingData(entry);
      onOpenDrawing && onOpenDrawing(data, entry.fileName);
      onClose();
    } catch { setError('Не удалось открыть'); }
    finally { setActionId(null); }
  };

  const handleDelete = async (entry) => {
    setActionId(entry.id);
    try {
      await deleteDoc(doc(db, 'users', targetUser.uid, 'drawings', entry.id));
      setList(prev => prev.filter(i => i.id !== entry.id));
    } catch { setError('Не удалось удалить'); }
    finally { setActionId(null); }
  };

  const renderEntry = (entry) => {
    const isPdf = entry.fileFormat === 'pdf';
    const cloud = isCloudDrawing(entry);
    const ext = isPdf ? '.pdf' : '.ptCAD';
    return (
      <Box sx={{ px: 2.5, py: 1.4, background: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: '2rem', height: '2rem', flexShrink: 0, borderRadius: '0.4rem',
          background: isPdf ? 'rgba(21,101,192,0.08)' : 'rgba(211,47,47,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isPdf
            ? <PictureAsPdfIcon sx={{ fontSize: '1.1rem', color: '#1565c0' }} />
            : <InsertDriveFileOutlinedIcon sx={{ fontSize: '1.1rem', color: '#d32f2f' }} />}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.fileName || 'Без названия'}<Box component="span" sx={{ fontFamily: 'monospace', color: '#9e9e9e', fontWeight: 400 }}>{ext}</Box>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e' }}>{formatDate(entry.savedAt)}</Typography>
            <Box sx={{ fontSize: '0.65rem', color: '#9e9e9e', background: '#f5f5f5', borderRadius: '0.3rem', px: 0.6, py: 0.2 }}>
              {formatBytes(entry.byteSize)}
            </Box>
            {cloud && (
              <Box sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#1565c0', background: '#e3f2fd', borderRadius: '0.3rem', px: 0.6, py: 0.2 }}>
                История (облако)
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
          {isPdf ? (
            <Tooltip title="Открыть PDF">
              <span>
                <IconButton size="small" onClick={() => handleViewPdf(entry)} disabled={actionId === entry.id}
                  sx={{ color: '#1565c0', '&:hover': { background: 'rgba(21,101,192,0.08)' } }}>
                  <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : onOpenDrawing && (
            <Tooltip title="Открыть в редакторе">
              <span>
                <IconButton size="small" onClick={() => handleOpen(entry)} disabled={actionId === entry.id}
                  sx={{ color: '#d32f2f', '&:hover': { background: 'rgba(211,47,47,0.08)' } }}>
                  {actionId === entry.id ? <CircularProgress size={15} sx={{ color: '#d32f2f' }} /> : <FolderOpenIcon sx={{ fontSize: '1.1rem' }} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isPdf ? 'Скачать PDF' : 'Скачать .ptCAD'}>
            <span>
              <IconButton size="small" onClick={() => handleDownload(entry)} disabled={actionId === entry.id}
                sx={{ color: '#616161', '&:hover': { background: '#f5f5f5' } }}>
                <FileDownloadIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={cloud ? 'Удалить из истории в облаке' : 'Удалить копию с ПК'}>
            <span>
              <IconButton size="small" onClick={() => handleDelete(entry)} disabled={actionId === entry.id}
                sx={{
                  color: cloud ? '#e57373' : '#bdbdbd',
                  '&:hover': { color: '#d32f2f', background: 'rgba(211,47,47,0.06)' },
                }}>
                {actionId === entry.id ? <CircularProgress size={15} sx={{ color: '#d32f2f' }} /> : <DeleteOutlineIcon sx={{ fontSize: '1.1rem' }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const renderSection = (title, subtitle, entries, emptyText) => (
    <Box>
      <Box sx={{ px: 2.5, py: 1.25, background: '#f5f5f5', borderTop: '0.0625rem solid #eeeeee', borderBottom: '0.0625rem solid #eeeeee' }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#424242' }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', mt: 0.25 }}>{subtitle}</Typography>
      </Box>
      {entries.length === 0 ? (
        <Typography sx={{ fontSize: '0.78rem', color: '#bdbdbd', px: 2.5, py: 2 }}>{emptyText}</Typography>
      ) : (
        entries.map((entry, idx) => (
          <React.Fragment key={entry.id}>
            {renderEntry(entry)}
            {idx < entries.length - 1 && <Divider sx={{ mx: 2.5 }} />}
          </React.Fragment>
        ))
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1.5rem 4rem rgba(0,0,0,0.15)', maxHeight: '85vh' } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, background: '#fff', borderBottom: '0.0625rem solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton size="small" onClick={onClose} sx={{ color: '#9e9e9e', mr: 0.5 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ width: '2rem', height: '2rem', borderRadius: '0.4rem', background: 'linear-gradient(135deg,#d32f2f,#e53935)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <InsertDriveFileOutlinedIcon sx={{ color: '#fff', fontSize: '1.1rem' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#212121', lineHeight: 1.2 }}>
                Чертежи пользователя
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', lineHeight: 1, maxWidth: '18rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {targetUser?.email}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#9e9e9e' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ px: 2.5, py: 1.25, background: '#fff', borderBottom: '0.0625rem solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: '#f5f5f5', borderRadius: '0.5rem', px: 1.25, py: 0.6 }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: '0.9rem', color: '#9e9e9e' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по названию..."
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.82rem', color: '#212121', width: '100%' }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.2 }}>
                <CloseIcon sx={{ fontSize: '0.8rem', color: '#9e9e9e' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, background: '#fafafa', minHeight: '16rem', overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flexDirection: 'column', gap: 1.5 }}>
            <CircularProgress size={28} sx={{ color: '#d32f2f' }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>Загрузка...</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#d32f2f' }}>{error}</Typography>
            <Button size="small" onClick={fetchList} sx={{ mt: 1 }}>Повторить</Button>
          </Box>
        )}

        {!loading && !error && list.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6, flexDirection: 'column', gap: 1 }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: '3rem', color: '#e0e0e0' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#bdbdbd' }}>Нет сохранённых чертежей</Typography>
          </Box>
        )}

        {!loading && !error && list.length > 0 && (
          <Box>
            {renderSection(
              'История в облаке',
              'Сохранено пользователем через «В облако» — видно в его «Истории чертежей».',
              cloudList,
              search.trim() ? 'Нет совпадений в облаке' : 'Нет чертежей в облаке',
            )}
            {renderSection(
              'Сохранено на компьютер',
              'Копии при «На устройство» и экспорте PDF. Можно удалять без влияния на историю в облаке.',
              deviceList,
              search.trim() ? 'Нет совпадений среди копий с ПК' : 'Пока нет копий с компьютера',
            )}
          </Box>
        )}
      </DialogContent>

      <Box sx={{ background: '#fff', borderTop: '0.0625rem solid #e0e0e0', px: 3, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.7rem', color: '#bdbdbd' }}>
          {list.length > 0
            ? (search.trim()
              ? `Найдено: ${filteredList.length} из ${list.length}`
              : `Всего ${list.length}: облако ${list.filter(isCloudDrawing).length}, с ПК ${list.filter((e) => !isCloudDrawing(e)).length}`)
            : ''}
        </Typography>
        <Button size="small" onClick={onClose} variant="outlined"
          sx={{ fontSize: '0.75rem', borderColor: '#e0e0e0', color: '#616161', '&:hover': { borderColor: '#bdbdbd', background: '#f5f5f5' } }}>
          Закрыть
        </Button>
      </Box>
    </Dialog>
  );
}

// ─── Вкладка: Пользователи ────────────────────────────────────────────────────
function UsersTab({ onOpenDrawing }) {
  const [users, setUsers] = useState([]);
  const [drawingCounts, setDrawingCounts] = useState({});
  const [deviceDrawingCounts, setDeviceDrawingCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingUid, setSavingUid] = useState(null);
  const [purgingAllDeviceCopies, setPurgingAllDeviceCopies] = useState(false);
  const [drawingsUser, setDrawingsUser] = useState(null);
  const [search, setSearch] = useState('');

  const totalDeviceCopies = Object.values(deviceDrawingCounts).reduce((sum, n) => sum + n, 0);

  const filteredUsers = search.trim()
    ? users.filter((u) => userMatchesSearch(u, search))
    : users;

  const loadDrawingCounts = useCallback(async () => {
    try {
      const snap = await getDocs(collectionGroup(db, 'drawings'));
      const counts = {};
      const deviceCounts = {};
      snap.forEach((d) => {
        const uid = d.ref.parent.parent?.id;
        if (!uid) return;
        counts[uid] = (counts[uid] || 0) + 1;
        if (d.data().origin === 'device') {
          deviceCounts[uid] = (deviceCounts[uid] || 0) + 1;
        }
      });
      setDrawingCounts(counts);
      setDeviceDrawingCounts(deviceCounts);
    } catch (e) {
      console.error('[UsersTab] drawing counts', e);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));
      setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })));
      await loadDrawingCounts();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [loadDrawingCounts]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handlePurgeAllDeviceDrawings = async () => {
    setPurgingAllDeviceCopies(true);
    try {
      await deleteAllDeviceDrawingCopiesGlobally();
      await loadDrawingCounts();
    } catch (e) {
      console.error('[UsersTab] purge all device drawings', e);
    } finally {
      setPurgingAllDeviceCopies(false);
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    setSavingUid(uid);
    try {
      await updateDoc(doc(db, 'users', uid), { role: newRole });
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, role: newRole } : u));
    } catch (e) {
      console.error(e);
    } finally {
      setSavingUid(null);
    }
  };

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e' }}>
          {search.trim()
            ? `Найдено: ${filteredUsers.length} из ${users.length}`
            : `${users.length} пользователей`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={purgingAllDeviceCopies ? <CircularProgress size={14} sx={{ color: '#9e9e9e' }} /> : <DeleteSweepIcon sx={{ fontSize: '0.95rem' }} />}
            onClick={handlePurgeAllDeviceDrawings}
            disabled={loading || purgingAllDeviceCopies || totalDeviceCopies === 0}
            sx={{ fontSize: '0.75rem', color: totalDeviceCopies > 0 ? '#616161' : '#bdbdbd' }}
          >
            Очистить копии с ПК{totalDeviceCopies > 0 ? ` (${totalDeviceCopies})` : ''}
          </Button>
          <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />} onClick={fetchUsers} disabled={loading} sx={{ fontSize: '0.75rem', color: '#9e9e9e' }}>Обновить</Button>
        </Box>
      </Box>

      {!loading && users.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', px: 1.25, py: 0.75 }}>
          <PeopleIcon sx={{ fontSize: '0.95rem', color: '#9e9e9e', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по email, инициалам, роли..."
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.82rem', color: '#212121', width: '100%' }}
          />
          {search && (
            <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.2, flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: '0.85rem', color: '#9e9e9e' }} />
            </IconButton>
          )}
        </Box>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: '#d32f2f' }} /></Box>}

      {!loading && (
        <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', overflow: 'hidden' }}>
          {filteredUsers.map((u, idx) => (
            <Box key={u.uid} sx={{ px: 2, py: 1.25, borderTop: idx > 0 ? '0.0625rem solid #f5f5f5' : 'none', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${ROLE_COLORS[u.role] || '#9e9e9e'}33, ${ROLE_COLORS[u.role] || '#9e9e9e'}66)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700, color: ROLE_COLORS[u.role] || '#9e9e9e',
              }}>
                {u.email?.[0]?.toUpperCase() || '?'}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.email}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e' }}>
                  {u.initials ? `${u.initials} · ` : ''}{fmtDate(u.createdAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                  <Tooltip title="Чертежи пользователя">
                    <IconButton size="small" onClick={() => setDrawingsUser(u)}
                      sx={{ color: '#d32f2f', '&:hover': { background: 'rgba(211,47,47,0.08)' } }}>
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </Tooltip>
                  {(drawingCounts[u.uid] || 0) > 0 && (
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#d32f2f', lineHeight: 1, userSelect: 'none' }}>
                      {drawingCounts[u.uid]}
                    </Typography>
                  )}
                </Box>
                {savingUid === u.uid
                  ? <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                  : (
                    <FormControl size="small" variant="outlined" sx={{ minWidth: '8rem' }}>
                      <Select
                        value={u.role || 'user'}
                        onChange={(e) => handleRoleChange(u.uid, e.target.value)}
                        disabled={u.role === 'admin'}
                        sx={{
                          fontSize: '0.75rem',
                          color: ROLE_COLORS[u.role] || '#616161',
                          fontWeight: 600,
                          borderRadius: '0.4rem',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8e8e8' },
                          '& .MuiSelect-select': { py: 0.6, px: 1 },
                        }}
                      >
                        <MenuItem value="admin" sx={{ fontSize: '0.8rem', color: '#d32f2f' }}>Администратор</MenuItem>
                        <MenuItem value="engineer" sx={{ fontSize: '0.8rem', color: '#1565c0' }}>Инженер</MenuItem>
                        <MenuItem value="user" sx={{ fontSize: '0.8rem' }}>Пользователь</MenuItem>
                      </Select>
                    </FormControl>
                  )
                }
              </Box>
            </Box>
          ))}
          {users.length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: '#bdbdbd', p: 2 }}>Нет пользователей</Typography>
          )}
          {users.length > 0 && filteredUsers.length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: '#bdbdbd', p: 2 }}>Ничего не найдено</Typography>
          )}
        </Box>
      )}

      <UserDrawingsDialog
        open={!!drawingsUser}
        onClose={() => { setDrawingsUser(null); loadDrawingCounts(); }}
        targetUser={drawingsUser}
        onOpenDrawing={onOpenDrawing}
      />
    </Box>
  );
}

// ─── Конфиг типов событий журнала ────────────────────────────────────────────
const EVENT_CONFIG = {
  login:         { label: 'Вход',            color: '#43a047', bg: '#e8f5e9', Icon: LoginIcon },
  session_start: { label: 'Начал работу',    color: '#00897b', bg: '#e0f2f1', Icon: PlayCircleOutlineIcon },
  session_end:   { label: 'Закончил работу', color: '#5d4037', bg: '#efebe9', Icon: StopCircleOutlinedIcon },
  logout:        { label: 'Выход',           color: '#757575', bg: '#f5f5f5', Icon: LogoutIcon },
  drawing_save:  { label: 'Сохранение',      color: '#1565c0', bg: '#e3f2fd', Icon: SaveIcon },
  drawing_open:  { label: 'Открытие',        color: '#f57c00', bg: '#fff3e0', Icon: OpenInBrowserIcon },
};

const SAVE_FORMAT_LABELS = {
  pdf: 'Сохранение PDF чертёжа',
  specification_pdf: 'Сохранение спецификации PDF',
  iges: 'Сохранение IGES (резка)',
  step: 'Сохранение STEP (сборка)',
  dxf: 'Сохранение DXF (резка)',
  dwg: 'Сохранение DWG (чертёж)',
};

function isSaveActivity(entry) {
  return entry.type === 'drawing_save' || entry.type === 'drawing_export';
}

function getActivityEventLabel(entry) {
  const cfg = EVENT_CONFIG[entry.type === 'drawing_export' ? 'drawing_save' : entry.type];
  if (!cfg) return entry.type;
  if (isSaveActivity(entry) && entry.format && entry.format !== 'ptCAD') {
    return SAVE_FORMAT_LABELS[entry.format] || `Сохранение (${entry.format})`;
  }
  if (isSaveActivity(entry)) {
    const dest = entry.destination === 'cloud'
      ? ' (облако)'
      : entry.destination === 'device'
        ? ' (устройство)'
        : '';
    return `Сохранение .ptCAD${dest}`;
  }
  if (entry.type === 'session_end' && entry.durationSec != null) {
    return `${cfg.label} · ${formatActivityDuration(entry.durationSec)}`;
  }
  return cfg.label;
}

function formatActivityDuration(sec) {
  if (sec == null || sec < 0) return '—';
  if (sec < 60) return `${sec} с`;
  if (sec < 3600) return `${Math.floor(sec / 60)} мин`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
}

const LOG_PAGE_SIZE = 25;

/** Журнал активности одного пользователя (по uid) */
function UserActivityLog({ uid }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const loadInitial = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'activityLog'),
        where('uid', '==', uid),
        orderBy('ts', 'desc'),
        limit(LOG_PAGE_SIZE),
      ));
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEntries(docs);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === LOG_PAGE_SIZE);
    } catch (e) {
      console.error('[UserActivityLog]', e);
      setEntries([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    setFilterType('all');
    setLastDoc(null);
    loadInitial();
  }, [loadInitial]);

  const loadMore = async () => {
    if (!uid || !lastDoc || loading) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'activityLog'),
        where('uid', '==', uid),
        orderBy('ts', 'desc'),
        startAfter(lastDoc),
        limit(LOG_PAGE_SIZE),
      ));
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEntries(prev => [...prev, ...docs]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === LOG_PAGE_SIZE);
    } catch (e) {
      console.error('[UserActivityLog] loadMore', e);
    } finally {
      setLoading(false);
    }
  };

  const displayed = entries.filter((e) => {
    if (filterType === 'all') return true;
    if (filterType === 'drawing_save') return isSaveActivity(e);
    return e.type === filterType;
  });

  return (
    <Box sx={{ px: 2, py: 1.5, background: '#fafafa', borderTop: '0.0625rem solid #f0f0f0' }}>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {['all', 'session_start', 'session_end', 'login', 'logout', 'drawing_save', 'drawing_open'].map(type => {
          const cfg = EVENT_CONFIG[type];
          const active = filterType === type;
          return (
            <Box
              key={type}
              onClick={() => setFilterType(type)}
              sx={{
                px: 1, py: 0.35, borderRadius: '1rem', fontSize: '0.65rem', fontWeight: active ? 700 : 400, cursor: 'pointer',
                background: active ? (cfg?.bg || '#e8e8e8') : '#fff',
                color: active ? (cfg?.color || '#212121') : '#9e9e9e',
                border: `0.0625rem solid ${active ? (cfg?.color || '#bdbdbd') + '55' : '#e8e8e8'}`,
                userSelect: 'none',
              }}
            >
              {type === 'all' ? 'Все' : (cfg?.label || type)}
            </Box>
          );
        })}
        <IconButton size="small" onClick={loadInitial} disabled={loading} sx={{ ml: 'auto', p: 0.3 }}>
          <RefreshIcon sx={{ fontSize: '0.85rem', color: '#9e9e9e' }} />
        </IconButton>
      </Box>

      {loading && entries.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} sx={{ color: '#d32f2f' }} /></Box>
      )}
      {!loading && displayed.length === 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: '#bdbdbd', py: 1 }}>Нет событий</Typography>
      )}
      {displayed.map((entry, idx) => {
        const cfgKey = entry.type === 'drawing_export' ? 'drawing_save' : entry.type;
        const cfg = EVENT_CONFIG[cfgKey] || { label: entry.type, color: '#9e9e9e', bg: '#f5f5f5', Icon: HistoryIcon };
        const Icon = cfg.Icon;
        return (
          <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.65, borderTop: idx > 0 ? '0.0625rem solid #f0f0f0' : 'none' }}>
            <Box sx={{ width: '1.6rem', height: '1.6rem', borderRadius: '0.35rem', flexShrink: 0, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon sx={{ fontSize: '0.85rem', color: cfg.color }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#212121' }}>{getActivityEventLabel(entry)}</Typography>
                {entry.source === 'local_file' && (
                  <Box sx={{ fontSize: '0.58rem', color: '#f57c00', background: '#fff3e0', px: 0.5, py: 0.05, borderRadius: '0.25rem' }}>локальный</Box>
                )}
                {entry.source === 'history' && (
                  <Box sx={{ fontSize: '0.58rem', color: '#1565c0', background: '#e3f2fd', px: 0.5, py: 0.05, borderRadius: '0.25rem' }}>история</Box>
                )}
              </Box>
              {entry.fileName && (
                <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.fileName}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd', flexShrink: 0 }}>{fmtDate(entry.ts)}</Typography>
          </Box>
        );
      })}
      {hasMore && displayed.length > 0 && (
        <Button size="small" onClick={loadMore} disabled={loading} sx={{ mt: 1, fontSize: '0.72rem', color: '#9e9e9e' }}>
          {loading ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
          Ещё
        </Button>
      )}
    </Box>
  );
}

// ─── Вкладка: Статистика ─────────────────────────────────────────────────────
function StatsTab() {
  // Реалтайм: данные пользователей (lastSeen, role)
  const [users, setUsers] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), orderBy('lastSeen', 'desc')),
      (snap) => setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })))
    );
    return () => unsub();
  }, []);

  const CHART_DAYS = 7;

  const [allSessions, setAllSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [expandedLogUid, setExpandedLogUid] = useState(null);

  const getEffectiveDuration = (s) => sessionDurationForStats(s);

  const sessionStartedAt = (s) => {
    if (!s.startedAt) return null;
    return s.startedAt?.toDate ? s.startedAt.toDate() : new Date(s.startedAt);
  };

  const fetchAllSessions = useCallback(async () => {
    setLoadingSessions(true);
    setFetchError(null);
    try {
      const snap = await getDocs(query(collection(db, 'sessions'), limit(5000)));
      setAllSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setFetchError(e.message || 'Ошибка загрузки');
      console.error('[StatsTab] sessions', e);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => { fetchAllSessions(); }, [fetchAllSessions]);

  // Все зарегистрированные аккаунты (коллекция users, realtime)
  const registeredCount = users.length;

  // Время пользователей и гостей — за всё время (в пределах загруженных сессий)
  const byUid = {};
  const byGuest = {};
  allSessions.forEach(s => {
    if (!s.uid) return;
    const dur = getEffectiveDuration(s);
    const ts = sessionStartedAt(s);
    if (s.isGuest) {
      if (!byGuest[s.uid]) byGuest[s.uid] = { uid: s.uid, totalSec: 0, sessions: 0, lastAt: null };
      byGuest[s.uid].sessions++;
      if (dur != null) byGuest[s.uid].totalSec += dur;
      if (ts && (!byGuest[s.uid].lastAt || ts > byGuest[s.uid].lastAt)) byGuest[s.uid].lastAt = ts;
    } else {
      if (!byUid[s.uid]) byUid[s.uid] = { totalSec: 0, sessions: 0 };
      byUid[s.uid].sessions++;
      if (dur != null) byUid[s.uid].totalSec += dur;
    }
  });
  const guestStatsAllTime = Object.values(byGuest).sort((a, b) => b.totalSec - a.totalSec);
  const allTimeGuests = guestStatsAllTime.length;

  // Объединённый список: зарегистрированные пользователи + данные о времени
  // Сортируем: сначала онлайн, потом по суммарному времени
  const mergedList = [...users].sort((a, b) => {
    const aOn = isOnline(a.lastSeen) ? 1 : 0;
    const bOn = isOnline(b.lastSeen) ? 1 : 0;
    if (aOn !== bOn) return bOn - aOn;
    const aT = byUid[a.uid]?.totalSec || 0;
    const bT = byUid[b.uid]?.totalSec || 0;
    return bT - aT;
  });

  const maxTime = Math.max(...mergedList.map(u => byUid[u.uid]?.totalSec || 0), 1);
  const onlineCount = users.filter(u => isOnline(u.lastSeen)).length;

  // График: последние 7 дней
  const chartSince = new Date(Date.now() - CHART_DAYS * 24 * 3600 * 1000);
  const dayMap = {};
  for (let i = 0; i < CHART_DAYS; i++) {
    const d = new Date(Date.now() - i * 24 * 3600 * 1000);
    dayMap[d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })] = { totalSec: 0, uids: {} };
  }
  allSessions.forEach(s => {
    const ts = sessionStartedAt(s);
    if (!ts || ts < chartSince) return;
    const key = ts.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
    if (!(key in dayMap)) return;
    const dur = getEffectiveDuration(s);
    if (dur != null) dayMap[key].totalSec += dur;
    if (s.uid) dayMap[key].uids[s.uid] = true;
  });
  const dayEntries = Object.entries(dayMap)
    .reverse()
    .map(([day, v]) => ({
      day,
      totalSec: v.totalSec,
      hours: v.totalSec / 3600,
      people: Object.keys(v.uids).length,
    }));
  const maxHours = Math.max(...dayEntries.map(d => d.hours), 0.01);
  const maxPeople = Math.max(...dayEntries.map(d => d.people), 1);

  const fmtChartHours = (sec) => {
    if (!sec) return '0 ч';
    if (sec < 3600) return `${Math.round(sec / 60)} мин`;
    const h = Math.floor(sec / 3600);
    const m = Math.round((sec % 3600) / 60);
    return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
  };

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* График: часы + люди за 7 дней */}
      <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Активность за 7 дней
          </Typography>
          <Button size="small" startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={fetchAllSessions}
            disabled={loadingSessions}
            sx={{ fontSize: '0.72rem', color: '#9e9e9e', minWidth: 0, py: 0.25 }}>
            Обновить
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '0.15rem', background: 'linear-gradient(180deg,#e53935,#ef9a9a)' }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#757575' }}>Часы в программе</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: '0.5rem', height: '0.5rem', borderRadius: '0.15rem', background: 'linear-gradient(180deg,#1565c0,#64b5f6)' }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#757575' }}>Уникальные пользователи</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.25rem', height: '4.5rem' }}>
          {dayEntries.map(({ day, totalSec, hours, people }) => (
            <Tooltip
              key={day}
              title={`${day}: ${fmtChartHours(totalSec)} · ${people} чел.`}
              arrow
            >
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35, cursor: 'default', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '0.15rem', width: '100%', height: '3.6rem' }}>
                  <Box sx={{
                    width: '42%', maxWidth: '1.1rem', borderRadius: '0.2rem 0.2rem 0 0',
                    background: hours > 0 ? 'linear-gradient(180deg,#e53935,#ef9a9a)' : '#f0f0f0',
                    height: `${Math.max((hours / maxHours) * 100, hours > 0 ? 8 : 4)}%`,
                    minHeight: hours > 0 ? '0.25rem' : '0.15rem',
                    transition: 'height 0.3s',
                  }} />
                  <Box sx={{
                    width: '42%', maxWidth: '1.1rem', borderRadius: '0.2rem 0.2rem 0 0',
                    background: people > 0 ? 'linear-gradient(180deg,#1565c0,#64b5f6)' : '#f0f0f0',
                    height: `${Math.max((people / maxPeople) * 100, people > 0 ? 8 : 4)}%`,
                    minHeight: people > 0 ? '0.25rem' : '0.15rem',
                    transition: 'height 0.3s',
                  }} />
                </Box>
                <Typography sx={{ fontSize: '0.55rem', color: '#bdbdbd', lineHeight: 1, textAlign: 'center' }}>{day}</Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Карточки: онлайн + уникальные за всё время */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', p: 1.5, flex: '1 1 5rem', minWidth: '5.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ color: '#43a047', fontSize: '1rem', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#212121', lineHeight: 1 }}>{onlineCount}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', lineHeight: 1.2 }}>онлайн сейчас</Typography>
          </Box>
        </Box>
        <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', p: 1.5, flex: '1 1 5rem', minWidth: '5.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ color: '#d32f2f', fontSize: '1rem', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            {users.length === 0
              ? <CircularProgress size={18} sx={{ color: '#d32f2f', my: 0.5 }} />
              : <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#212121', lineHeight: 1 }}>{registeredCount}</Typography>
            }
            <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', lineHeight: 1.2 }}>авторизовавшиеся</Typography>
            <Typography sx={{ fontSize: '0.6rem', color: '#bdbdbd' }}>за всё время</Typography>
          </Box>
        </Box>
        <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', p: 1.5, flex: '1 1 5rem', minWidth: '5.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOutlineIcon sx={{ color: '#9e9e9e', fontSize: '1rem', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            {loadingSessions
              ? <CircularProgress size={18} sx={{ color: '#9e9e9e', my: 0.5 }} />
              : <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#212121', lineHeight: 1 }}>{allTimeGuests}</Typography>
            }
            <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e', lineHeight: 1.2 }}>гости</Typography>
            <Typography sx={{ fontSize: '0.6rem', color: '#bdbdbd' }}>за всё время</Typography>
          </Box>
        </Box>
      </Box>

      {/* Ошибка сессий */}
      {fetchError && (
        <Box sx={{ background: '#fff3f3', border: '0.0625rem solid #ffcdd2', borderRadius: '0.5rem', p: 2 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#d32f2f', fontWeight: 600, mb: 0.5 }}>Ошибка загрузки сессий</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#b71c1c', fontFamily: 'monospace' }}>{fetchError}</Typography>
        </Box>
      )}

      {/* ── Объединённый список пользователей ── */}
      <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Пользователи
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd' }}>за всё время</Typography>
        </Box>
        {users.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} sx={{ color: '#d32f2f' }} /></Box>
        )}
        {mergedList.map((u) => {
          const isOn = isOnline(u.lastSeen);
          const stat = byUid[u.uid];
          const totalSec = stat?.totalSec || 0;
          const sessCount = stat?.sessions || 0;
          const pct = (totalSec / maxTime) * 100;
          const logOpen = expandedLogUid === u.uid;
          return (
            <Box key={u.uid} sx={{ borderTop: '0.0625rem solid #f5f5f5' }}>
              <Box sx={{ px: 2, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.25, mb: totalSec > 0 && !logOpen ? 0.5 : 0 }}>
                {/* Аватар + индикатор онлайн */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Box sx={{
                    width: '2rem', height: '2rem', borderRadius: '50%',
                    background: `linear-gradient(135deg, ${ROLE_COLORS[u.role] || '#9e9e9e'}33, ${ROLE_COLORS[u.role] || '#9e9e9e'}66)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', fontWeight: 700, color: ROLE_COLORS[u.role] || '#9e9e9e',
                  }}>
                    {u.email?.[0]?.toUpperCase() || '?'}
                  </Box>
                  <FiberManualRecordIcon sx={{ position: 'absolute', bottom: '-0.1rem', right: '-0.1rem', fontSize: '0.75rem', color: isOn ? '#43a047' : '#bdbdbd' }} />
                </Box>
                {/* Email + статус */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: isOn ? '#43a047' : '#bdbdbd' }}>
                    {isOn ? 'онлайн' : `был(а): ${fmtDate(u.lastSeen)}`}
                  </Typography>
                </Box>
                {/* Время + сессии */}
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  {loadingSessions
                    ? <CircularProgress size={12} sx={{ color: '#e0e0e0' }} />
                    : <>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: totalSec > 0 ? '#212121' : '#e0e0e0' }}>
                          {totalSec > 0 ? fmtDuration(totalSec) : '—'}
                        </Typography>
                        {sessCount > 0 && <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e' }}>{sessCount} сесс.</Typography>}
                      </>
                  }
                </Box>
                <Tooltip title={logOpen ? 'Скрыть журнал' : 'Журнал действий'}>
                  <IconButton
                    size="small"
                    onClick={() => setExpandedLogUid(logOpen ? null : u.uid)}
                    sx={{ color: logOpen ? '#d32f2f' : '#bdbdbd', flexShrink: 0, '&:hover': { color: '#d32f2f', background: 'rgba(211,47,47,0.06)' } }}
                  >
                    <HistoryIcon sx={{ fontSize: '1.05rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Прогресс-бар времени */}
              {totalSec > 0 && !logOpen && (
                <Box sx={{ ml: '3.25rem', mr: 2, mb: 0.5, height: '0.2rem', background: '#f5f5f5', borderRadius: '0.1rem', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${ROLE_COLORS[u.role] || '#9e9e9e'}, ${ROLE_COLORS[u.role] || '#9e9e9e'}88)`, borderRadius: '0.1rem', transition: 'width 0.5s' }} />
                </Box>
              )}
              {logOpen && <UserActivityLog uid={u.uid} />}
            </Box>
          );
        })}
      </Box>

      {/* Гости: время каждого за всё время */}
      <Box sx={{ background: '#fff', border: '0.0625rem solid #e8e8e8', borderRadius: '0.5rem', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Гости
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd' }}>за всё время</Typography>
        </Box>
        {loadingSessions && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: '#9e9e9e' }} /></Box>
        )}
        {!loadingSessions && guestStatsAllTime.length === 0 && (
          <Typography sx={{ fontSize: '0.8rem', color: '#bdbdbd', px: 2, pb: 2 }}>Нет гостевых визитов</Typography>
        )}
        {!loadingSessions && guestStatsAllTime.length > 0 && (() => {
          const maxGuestTime = Math.max(...guestStatsAllTime.map(g => g.totalSec), 1);
          return guestStatsAllTime.map((g, idx) => {
            const pct = (g.totalSec / maxGuestTime) * 100;
            const shortId = g.uid.length > 12 ? `…${g.uid.slice(-8)}` : g.uid;
            const guestLogKey = `guest:${g.uid}`;
            const guestLogOpen = expandedLogUid === guestLogKey;
            return (
              <Box key={g.uid} sx={{ borderTop: '0.0625rem solid #f5f5f5' }}>
                <Box sx={{ px: 2, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.25, mb: g.totalSec > 0 && !guestLogOpen ? 0.5 : 0 }}>
                  <Box sx={{
                    width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0, background: '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <PersonOutlineIcon sx={{ fontSize: '1rem', color: '#9e9e9e' }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Tooltip title={g.uid} arrow>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
                        Гость {idx + 1} <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#9e9e9e', fontWeight: 400 }}>{shortId}</Box>
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontSize: '0.68rem', color: '#bdbdbd' }}>
                      {g.lastAt ? `последний визит: ${fmtDate(g.lastAt)}` : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: g.totalSec > 0 ? '#212121' : '#e0e0e0' }}>
                      {g.totalSec > 0 ? fmtDuration(g.totalSec) : '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#9e9e9e' }}>{g.sessions} сесс.</Typography>
                  </Box>
                  <Tooltip title={guestLogOpen ? 'Скрыть журнал' : 'Журнал (если есть)'}>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedLogUid(guestLogOpen ? null : guestLogKey)}
                      sx={{ color: guestLogOpen ? '#d32f2f' : '#bdbdbd', flexShrink: 0, '&:hover': { color: '#d32f2f', background: 'rgba(211,47,47,0.06)' } }}
                    >
                      <HistoryIcon sx={{ fontSize: '1.05rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {g.totalSec > 0 && !guestLogOpen && (
                  <Box sx={{ ml: '3.25rem', mr: 2, mb: 0.5, height: '0.2rem', background: '#f5f5f5', borderRadius: '0.1rem', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${pct}%`, background: '#bdbdbd', borderRadius: '0.1rem', transition: 'width 0.5s' }} />
                  </Box>
                )}
                {guestLogOpen && <UserActivityLog uid={g.uid} />}
              </Box>
            );
          });
        })()}
      </Box>

    </Box>
  );
}

// ─── Утилиты аналитики ───────────────────────────────────────────────────────
function fmtDuration(sec) {
  if (sec == null || sec < 0) return '—';
  if (sec < 60) return `${sec} с`;
  if (sec < 3600) return `${Math.floor(sec / 60)} мин ${sec % 60} с`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h} ч ${m} мин`;
}


// ─── Главный диалог ───────────────────────────────────────────────────────────
export default function AdminPanel({ open, onClose, onOpenDrawing }) {
  const [tab, setTab] = useState(0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1.5rem 4rem rgba(0,0,0,0.15)', maxHeight: '85vh' } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, background: '#fff', borderBottom: '0.0625rem solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: '2.25rem', height: '2.25rem', borderRadius: '0.5rem', background: 'linear-gradient(135deg, #d32f2f, #e53935)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ShieldIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#212121', lineHeight: 1.2 }}>Администрирование</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', lineHeight: 1 }}>Только для администратора</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#9e9e9e', '&:hover': { color: '#212121', background: '#f5f5f5' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            background: '#fff',
            borderBottom: '0.0625rem solid #e0e0e0',
            minHeight: '2.5rem',
            '& .MuiTab-root': { fontSize: '0.78rem', minHeight: '2.5rem', textTransform: 'none', fontWeight: 500 },
            '& .Mui-selected': { color: '#d32f2f', fontWeight: 700 },
            '& .MuiTabs-indicator': { backgroundColor: '#d32f2f' },
          }}
        >
          <Tab icon={<StorageIcon sx={{ fontSize: '0.9rem' }} />} iconPosition="start" label="Хранилище" />
          <Tab icon={<PeopleIcon sx={{ fontSize: '0.9rem' }} />} iconPosition="start" label="Пользователи" />
          <Tab icon={<BarChartIcon sx={{ fontSize: '0.9rem' }} />} iconPosition="start" label="Статистика" />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ p: 0, background: '#fafafa', overflowY: 'auto' }}>
        {tab === 0 && <StorageTab />}
        {tab === 1 && <UsersTab onOpenDrawing={onOpenDrawing} />}
        {tab === 2 && <StatsTab />}
      </DialogContent>
    </Dialog>
  );
}
